import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { PlatformSelect } from './steps/PlatformSelect';
import { TopicSelect } from './steps/TopicSelect';
import { UploadAssets } from './steps/UploadAssets';
import { PresetSelect } from './steps/PresetSelect';
import { ModelSelect } from './steps/ModelSelect';
import { StyleSelect } from './steps/StyleSelect';
import { VoiceSelect } from './steps/VoiceSelect';
import { Settings } from './steps/Settings';
import { Preview } from './steps/Preview';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

const steps = [
  { id: 'platform', title: 'Platform' },
  { id: 'topic', title: 'Topic' },
  { id: 'upload', title: 'Media' },
  { id: 'preset', title: 'Preset' },
  { id: 'model', title: 'Visual Style' },
  { id: 'voice', title: 'Voice' },
  { id: 'style', title: 'Direction' },
  { id: 'settings', title: 'Settings' },
  { id: 'preview', title: 'Preview' },
];

// Steps to skip for upload mode (user provides their own media)
const UPLOAD_SKIP_STEP_IDS = new Set(['preset', 'model', 'style']);

export type CreationMode = 'import' | 'custom' | 'upload';

export type UploadedAsset = {
  fileKey: string;
  url: string;
  duration: number;
  description: string;
  script: string;
  fit: 'cover' | 'contain';
};

export type ContentFormData = {
  creationMode?: CreationMode;
  platform: string;
  orientation: 'vertical' | 'landscape';
  socialHandle?: string;
  topic: string;
  title?: string;
  topicName?: string;
  customPrompt?: string;
  enhancedPrompt?: string;
  voice: string;
  language: string;
  duration: number;
  sceneCount: number;
  sceneLength: number;
  email?: string;
  presetId?: string;
  visualModel?: string;
  recraftStyle?: string;
  style?: string;
  styleNotes?: string;
  sourceUrl?: string;
  sourceType?: string;
  uploadedImages?: File[];
  uploadedScript?: string;
  uploadedAssets?: UploadedAsset[];
};

export function ContentWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepValid, setIsStepValid] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [previewCreateCallback, setPreviewCreateCallback] = useState<(() => Promise<number>) | null>(null);
  const [formData, setFormData] = useState<ContentFormData>({
    platform: '',
    orientation: 'vertical',
    topic: '',
    voice: '',
    language: 'en',
    duration: 30,
    sceneCount: 3,
    sceneLength: 10,
    presetId: 'none',
    visualModel: 'auto',
    style: 'An even mix of clear information and engaging delivery.',
    styleNotes: '',
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  const shouldSkipStep = (stepIndex: number) => {
    const id = steps[stepIndex].id;
    // Upload mode skips preset/model/style; every other mode skips the upload step.
    return formData.creationMode === 'upload'
      ? UPLOAD_SKIP_STEP_IDS.has(id)
      : id === 'upload';
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  const updateFormData = useCallback((data: Partial<ContentFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      let next = currentStep + 1;
      // Skip steps that don't apply to this creation mode
      while (next < steps.length - 1 && shouldSkipStep(next)) {
        next++;
      }
      setCurrentStep(next);
      setIsStepValid(false);
    } else {
      handleCreateContent();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      let prev = currentStep - 1;
      // Skip steps that don't apply to this creation mode
      while (prev > 0 && shouldSkipStep(prev)) {
        prev--;
      }
      setCurrentStep(prev);
      
      // When going back, check if the step has already been completed
      const prevStepId = steps[prev].id;
      
      // For steps that should be considered valid when returning to them
      if (
        prevStepId === 'platform' && formData.platform ||
        prevStepId === 'topic' && (formData.customPrompt || formData.creationMode === 'upload') ||
        prevStepId === 'upload' && Boolean(formData.uploadedAssets?.length) ||
        prevStepId === 'preset' || // Preset always has a default selected
        prevStepId === 'model' && formData.visualModel ||
        prevStepId === 'style' || // Style is always valid
        prevStepId === 'voice' && formData.voice ||
        prevStepId === 'settings' || // Settings is always valid
        prevStepId === 'preview' // Preview is always valid
      ) {
        setIsStepValid(true);
      } else {
        setIsStepValid(false);
      }
    }
  };

  const handleCreateContent = async (): Promise<number> => {
    try {
      setIsCreating(true);
      
      // Use explicit title if set (e.g. from thread import), otherwise derive from topic
      const displayTitle = formData.title
        || (formData.topicName 
          ? formData.topicName.charAt(0).toUpperCase() + formData.topicName.slice(1)
          : formData.topic.charAt(0).toUpperCase() + formData.topic.slice(1));

      // Upload mode: create from user-provided media + script (no AI scenes).
      if (formData.creationMode === 'upload') {
        const assets = formData.uploadedAssets || [];
        const uploadRes = await api.post('/content/create-from-upload', {
          platform: formData.platform,
          topic: formData.topic,
          voice: formData.voice,
          language: formData.language,
          email: formData.email,
          social_handle: formData.socialHandle,
          title: displayTitle,
          preset_id: formData.presetId === 'none' ? undefined : formData.presetId,
          assets: assets.map(a => ({
            file_key: a.fileKey,
            media_type: 'image',
            duration: a.duration,
            description: a.description,
            script: a.script,
            fit: a.fit,
          })),
        });
        const uploadedContentId = uploadRes.data.id;
        if (!uploadedContentId) throw new Error('No content ID returned from server');
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate('/dashboard');
        return uploadedContentId;
      }

      const response = await api.post('/content/create', {
        platform: formData.platform,
        topic: formData.topic, // Keep original lowercase topic
        title: displayTitle,   // Add capitalized title
        prompt: formData.customPrompt,
        enhanced_prompt: formData.enhancedPrompt,
        voice: formData.voice,
        language: formData.language,
        duration: formData.duration,
        num_scenes: formData.sceneCount,
        target_scene_duration: formData.sceneLength,
        email: formData.email,
        social_handle: formData.socialHandle,
        visual_model: formData.visualModel,
        preset_id: formData.presetId === 'none' ? undefined : formData.presetId,
        visual_style: formData.recraftStyle,
        style: formData.style,
        style_notes: formData.styleNotes && formData.styleNotes.trim() ? formData.styleNotes : undefined,
      });

      const contentId = response.data.id;
      if (!contentId) {
        throw new Error('No content ID returned from server');
      }

      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/dashboard');
      return contentId;
    } catch (error: any) {
      console.error('Error creating content:', error);
      
      // Handle insufficient credits error (402 Payment Required)
      if (error.response?.status === 402) {
        const detail = error.response.data.detail || {};
        toast({
          title: 'Insufficient Credits',
          description: `You need ${detail.required || 'more'} credits, but you only have ${detail.available || 0} credits available. Please upgrade your plan to continue.`,
          variant: 'destructive',
          action: (
            <Button 
              variant="secondary" 
              size="sm" 
              className="text-white bg-primary hover:bg-primary/90 border-0"
              onClick={() => navigate('/pricing')}
            >
              Upgrade Plan
            </Button>
          ),
          duration: 10000, // Longer duration so user has time to click the button
        });
      } else if (
        error.response?.status === 422 &&
        typeof error.response.data?.detail === 'string'
      ) {
        // Content moderation (or other single-message 422) — show the reason.
        toast({
          title: "Can't create this video",
          description: error.response.data.detail,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create content. Please try again.',
          variant: 'destructive',
        });
      }
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const getCurrentStep = () => {
    switch (steps[currentStep].id) {
      case 'platform':
        return (
          <PlatformSelect
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'topic':
        return (
          <TopicSelect
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'upload':
        return (
          <UploadAssets
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'preset':
        return (
          <PresetSelect
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'model':
        return (
          <ModelSelect
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'voice':
        return (
          <VoiceSelect
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'style':
        return (
          <StyleSelect
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'settings':
        return (
          <Settings
            formData={formData}
            updateFormData={updateFormData}
            onValidationChange={setIsStepValid}
          />
        );
      case 'preview':
        return (
          <Preview
            formData={formData}
            onCreateContent={handleCreateContent}
            onComplete={handlePreviewComplete}
          />
        );
      default:
        return null;
    }
  };

  // Stable callback passed to Preview so its useEffect doesn't fire on every render
  const handlePreviewComplete = useCallback((callback: () => Promise<number>) => {
    setPreviewCreateCallback(() => callback);
  }, []);

  // Set preview step valid without calling setState during render
  useEffect(() => {
    if (steps[currentStep].id === 'preview' && !isStepValid) {
      setIsStepValid(true);
    }
  }, [currentStep, isStepValid]);

  // Reference to the Preview component's create content function
  const getCreateContentAction = () => {
    if (currentStep === steps.length - 1 && previewCreateCallback) {
      return previewCreateCallback;
    }
    return handleNext;
  };

  return (
    <div className="container max-w-4xl py-6 md:py-10">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-4">Create Content</h1>
        
        {/* Steps - Desktop */}
        <div className="hidden md:flex justify-between mt-2 text-sm text-muted-foreground">
          {steps.map((step, index) => {
            const isSkipped = shouldSkipStep(index);
            return (
              <span
                key={step.id}
                className={`transition-all ${
                  isSkipped
                    ? 'line-through opacity-40'
                    : index <= currentStep
                      ? 'text-primary'
                      : ''
                }`}
              >
                {step.title}
              </span>
            );
          })}
        </div>

        {/* Steps - Mobile */}
        <div className="flex md:hidden items-center justify-between text-sm text-muted-foreground">
          <span className="text-primary font-medium">
            {steps[currentStep].title}
          </span>
          <span className="text-xs">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300 ease-in-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 md:p-6 mb-6 min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {getCurrentStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex justify-between items-center gap-3">
        {currentStep > 0 && (
          <Button
            variant="outline"
            onClick={handleBack}
            className="h-10"
          >
            <ArrowLeft className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Back</span>
          </Button>
        )}
        <div className="flex-1" />
        <Button 
          onClick={() => {
            if (currentStep === steps.length - 1 && previewCreateCallback) {
              previewCreateCallback();
            } else {
              handleNext();
            }
          }}
          disabled={!isStepValid}
          className={`h-10 ${
            currentStep === steps.length - 1 
              ? 'bg-primary hover:bg-primary/90 animate-pulse shadow-lg hover:cursor-pointer hover:scale-105 transition-all' 
              : ''
          }`}
        >
          {currentStep === steps.length - 1 ? (
            isCreating ? (
              <>
                <Loader2 className="h-4 w-4 md:mr-2 animate-spin" />
                <span className="hidden md:inline">Creating...</span>
                <span className="md:hidden">...</span>
              </>
            ) : (
              <>
                <span className="hidden md:inline">Create Content</span>
                <span className="md:hidden">Create</span>
              </>
            )
          ) : (
            <>
              <span className="hidden md:inline">Next</span>
              <ArrowRight className="h-4 w-4 md:ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
